/**
 * Security ACL Processor for AI Agents
 *
 * Handles automatic generation of sys_security_acl and sys_security_acl_role records
 * from a SecurityAclUserAccessConfig definition.
 *
 * Does NOT generate sys_agent_access_role_configuration — use dataAccess for that.
 */

import { isGUID, type ObjectShape, type Record, type Shape } from '@servicenow/sdk-build-core'
import type { Context } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import {
    ACL_DEFAULTS,
    GEN_AI_AGENT_TYPE,
    PUBLIC_ACCESS_ROLE_SYS_ID,
    SYS_SECURITY_ACL_ROLE,
    SYS_USER_ROLE,
    USER_IS_AUTHENTICATED,
} from './constants'

const SYS_SECURITY_ACL = 'sys_security_acl'

/**
 * Processes a securityAcl config object and creates the corresponding platform records.
 *
 * Creates:
 * - One `sys_security_acl` record (name = aclName, type = Generative AI Agent, operation = execute)
 * - One `sys_security_acl_role` record per role in `roles`
 *
 * @param securityAcl  - The securityAcl ObjectShape from the Fluent definition
 * @param aclName      - The name to use for the ACL record (should match internal_name of agent)
 * @param factory      - SDK record factory
 * @param diagnostics  - SDK diagnostics for error reporting
 * @returns aclRecord  - The created sys_security_acl record (its ID must be set on the agent's acl field)
 * @returns allRecords - All created records (acl + role records) to add as child records
 */
export async function processSecurityAcl(
    securityAcl: ObjectShape,
    aclName: string,
    factory: Context['factory'],
    diagnostics: Context['diagnostics']
): Promise<{ aclRecord: Record; allRecords: Record[] }> {
    const records: Record[] = []

    const accessType = securityAcl.get('type').asString().getValue()

    // Validate access type
    if (accessType !== 'Any authenticated user' && accessType !== 'Specific role' && accessType !== 'Public') {
        diagnostics.error(
            securityAcl,
            `securityAcl.type must be 'Any authenticated user', 'Specific role', or 'Public'. Got: '${accessType}'`
        )
    }

    // Map access type to security_attribute value
    const securityAttribute = accessType === 'Any authenticated user' ? USER_IS_AUTHENTICATED : ''

    // Collect roles to create sys_security_acl_role records
    let aclRoles: Shape[] = []
    const rolesField = securityAcl.has('roles') ? securityAcl.get('roles') : undefined

    if (accessType === 'Specific role') {
        if (!rolesField || !rolesField.isArray()) {
            diagnostics.error(securityAcl, "securityAcl.roles must be an array when type is 'Specific role'")
        } else {
            aclRoles = rolesField.asArray().getElements()
            if (aclRoles.length === 0) {
                diagnostics.error(rolesField, 'securityAcl.roles must contain at least one role')
            }
        }
    }

    // Extract optional $id for stable ACL record identity
    const aclExplicitId = securityAcl.has('$id') ? securityAcl.get('$id') : undefined

    // Create sys_security_acl record
    const aclRecord = await factory.createRecord({
        source: securityAcl,
        table: SYS_SECURITY_ACL,
        ...(aclExplicitId instanceof NowIdShape ? { explicitId: aclExplicitId } : {}),
        properties: securityAcl.transform(({ $ }) => ({
            name: $.val(aclName),
            type: $.val(GEN_AI_AGENT_TYPE),
            operation: $.val(ACL_DEFAULTS.operation),
            active: $.val(ACL_DEFAULTS.active),
            admin_overrides: $.val(ACL_DEFAULTS.adminOverrides),
            advanced: $.val(ACL_DEFAULTS.advanced),
            decision_type: $.val(ACL_DEFAULTS.decisionType),
            local_or_existing: $.val(ACL_DEFAULTS.localOrExisting),
            security_attribute: $.val(securityAttribute),
        })),
    })
    records.push(aclRecord)

    // For 'Public' type, always create one sys_security_acl_role with the public access role
    if (accessType === 'Public') {
        const publicRoleReference = await factory.createReference({
            source: securityAcl,
            table: SYS_USER_ROLE,
            guid: PUBLIC_ACCESS_ROLE_SYS_ID,
        })
        const publicRoleRecord = await factory.createRecord({
            source: securityAcl,
            table: SYS_SECURITY_ACL_ROLE,
            properties: {
                sys_security_acl: aclRecord,
                sys_user_role: publicRoleReference,
            },
        })
        records.push(publicRoleRecord)
    }

    // Create sys_security_acl_role records — one per role
    // Supports role names (e.g. 'itil') resolved via DB lookup, or sys_id GUIDs
    for (const role of aclRoles) {
        const roleReference = role.isString()
            ? await factory.createReference({
                  source: role,
                  table: SYS_USER_ROLE,
                  keys: isGUID(role.asString().getValue()) ? undefined : { name: role.asString().getValue() },
                  guid: isGUID(role.asString().getValue()) ? role.asString().getValue() : undefined,
              })
            : role

        const aclRoleRecord = await factory.createRecord({
            source: role,
            table: SYS_SECURITY_ACL_ROLE,
            properties: {
                sys_security_acl: aclRecord,
                sys_user_role: roleReference,
            },
        })
        records.push(aclRoleRecord)
    }

    return { aclRecord, allRecords: records }
}
